import React, { useState } from 'react';
import { useLazyQuery, useQuery, useApolloClient } from '@apollo/client';
import {
  AttachMoney,
  AutoAwesome,
  Check,
  CheckCircleOutline,
  Close,
  Description,
  ExpandLess,
  ExpandMore,
  HourglassEmpty,
  Info,
  LocalShipping,
  OpenInNew,
  Person,
  PlayArrow,
  StopCircleOutlined,
  TimerOff,
  Warning,
} from '@mui/icons-material';
import { amber, blue, blueGrey, green, grey, orange } from '@mui/material/colors';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { useNotifications } from 'modules/notification/hooks/useNotifications';
import { useSubprimeAgentSessionUpdated } from 'hooks/useSubscriptions';
import { START_SUBPRIME_AGENT_SESSION } from '../../api/startSubprimeAgent';
import { GET_SUBPRIME_AGENT_SESSIONS } from '../../api/getSubprimeAgentSessions';

interface DetailItem {
  label: string;
  subtitle: string;
  Icon: React.ElementType;
}

interface SubprimeAgentSession {
  id: string;
  sessionId: string;
  status: string;
  wantsFinancing: boolean | null;
  downpaymentFunds: number | null;
  collateral: string | null;
  guarantor: string | null;
  notes: string | null;
  name: string | null;
  phone: string | null;
  completedAt: string | null;
  stoppedByDealer: boolean;
  optedOut: boolean;
  createdAt: string;
  modifiedAt: string;
}

const buildDetailItems = (session: SubprimeAgentSession | null): DetailItem[] => [
  {
    label: 'Viability',
    subtitle:
      session?.wantsFinancing === true
        ? 'Wants financing'
        : session?.wantsFinancing === false
          ? 'Does not want financing'
          : 'Not evaluated yet',
    Icon: Check,
  },
  {
    label: 'Summary',
    subtitle: session?.notes ?? 'Will be generated',
    Icon: Description,
  },
  {
    label: 'Down Payment',
    subtitle: session?.downpaymentFunds != null ? `$${session.downpaymentFunds.toLocaleString()}` : 'Not collected',
    Icon: AttachMoney,
  },
  {
    label: 'Collateral',
    subtitle: session?.collateral ?? 'Not provided',
    Icon: LocalShipping,
  },
  {
    label: 'Guarantor',
    subtitle: session?.guarantor ?? 'Not discussed',
    Icon: Person,
  },
];

const REQUIRES_CONFIG_ITEMS = ['Compliance Assistant', 'Credit Evaluation Assistant', 'Protect IQ Assistant'];

const ASSISTANT_DESCRIPTION =
  'Automatically collect qualification details from the applicant and identify whether the deal has real funding potential';

interface StatusBadgeConfig {
  label: string;
  Icon: React.ElementType;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

const getStatusBadge = (session: SubprimeAgentSession | null, loading: boolean): StatusBadgeConfig => {
  if (loading) {
    return { label: '...', Icon: Info, bgColor: blueGrey[50], borderColor: blueGrey[100], textColor: blueGrey[600] };
  }
  if (!session) {
    return {
      label: 'Not Started',
      Icon: Close,
      bgColor: blueGrey[50],
      borderColor: blueGrey[100],
      textColor: blueGrey[600],
    };
  }
  switch (session.status) {
    case 'completed':
      return {
        label: 'Completed',
        Icon: CheckCircleOutline,
        bgColor: green[50],
        borderColor: green[200],
        textColor: green[800],
      };
    case 'awaiting_consent':
      return {
        label: 'Awaiting Consent',
        Icon: HourglassEmpty,
        bgColor: amber[50],
        borderColor: amber[200],
        textColor: amber[800],
      };
    case 'consent_expired':
      return {
        label: 'Consent Expired',
        Icon: TimerOff,
        bgColor: grey[100],
        borderColor: grey[300],
        textColor: grey[700],
      };
    case 'opted_out':
      return {
        label: 'Opted Out',
        Icon: StopCircleOutlined,
        bgColor: orange[50],
        borderColor: orange[200],
        textColor: orange[800],
      };
    case 'stopped':
      return {
        label: 'Stopped',
        Icon: StopCircleOutlined,
        bgColor: blueGrey[50],
        borderColor: blueGrey[100],
        textColor: blueGrey[600],
      };
    default:
      return { label: 'In Progress', Icon: PlayArrow, bgColor: blue[50], borderColor: blue[200], textColor: blue[800] };
  }
};

type LaunchPhase = 'idle' | 'waiting' | 'creating' | 'deployed';

const LAUNCH_STEPS: Array<{ phase: Exclude<LaunchPhase, 'idle'>; text: string }> = [
  { phase: 'waiting', text: 'Please wait while the Agent is created...' },
  { phase: 'creating', text: 'Creating agent...' },
  { phase: 'deployed', text: 'Agent deployed' },
];

const PHASE_ORDER: Array<Exclude<LaunchPhase, 'idle'>> = ['waiting', 'creating', 'deployed'];

export const StartSubprimeAgentDialog = () => {
  const { isOpen, handleClose, data } = useModal(ModalsKeys.StartSubprimeAgentDialog);
  const { showNotification } = useNotifications();
  const apolloClient = useApolloClient();
  const history = useHistory();
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [demoMode, setDemoMode] = useState(true);
  const [launchPhase, setLaunchPhase] = useState<LaunchPhase>('idle');

  const {
    data: sessionsData,
    loading: sessionsLoading,
    refetch: refetchSessions,
  } = useQuery(GET_SUBPRIME_AGENT_SESSIONS, {
    variables: { vendorOpportunityId: data?.voId },
    skip: !isOpen || !data?.voId,
    fetchPolicy: 'cache-and-network',
  });

  useSubprimeAgentSessionUpdated(isOpen ? (data?.voId ?? null) : null, refetchSessions);

  const latestSession: SubprimeAgentSession | null = sessionsData?.subprimeAgent?.getSessionsByVo?.[0] ?? null;

  const detailItems = buildDetailItems(latestSession);

  const statusBadge = getStatusBadge(latestSession, sessionsLoading);
  const hasSession = latestSession !== null;

  const [startSession, { loading }] = useLazyQuery(START_SUBPRIME_AGENT_SESSION, {
    fetchPolicy: 'network-only',
  });

  const handleOpenMessages = () => {
    handleClose();
    data?.onOpenAgentConversation?.();
  };

  const handleStartAssistant = async () => {
    const result = await startSession({
      variables: {
        input: {
          vendorOpportunityId: data?.voId,
          demoMode,
        },
      },
    });

    const response = result.data?.subprimeAgent?.startSession;

    if (!response?.success) {
      showNotification(response?.message ?? 'Failed to start AI Agent session', { type: 'error' });
      return;
    }

    // startSession succeeded — play the 10-second deployment animation
    setLaunchPhase('waiting');

    const timer1 = setTimeout(() => setLaunchPhase('creating'), 3500);
    const timer2 = setTimeout(() => setLaunchPhase('deployed'), 7500);

    try {
      await new Promise<void>(resolve => setTimeout(resolve, 10000));

      // Navigate to the Activity tab so DealActivityCard mounts and fetches the
      // updated list. Do NOT call openAgentConversationRef via onOpenAgentConversation
      // — that ref still points to the old activity and would open the wrong conversation.
      history.push({ hash: '#activity' });
      // Yield one tick so React processes the navigation and mounts DealActivityCard
      // (if the user was on a different tab) before refetchQueries looks for active watchers.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      apolloClient.refetchQueries({ include: ['DealActivities'] });
      showNotification('AI Agent session started successfully', { type: 'success' });
      handleClose();
    } finally {
      clearTimeout(timer1);
      clearTimeout(timer2);
      setLaunchPhase('idle');
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={launchPhase === 'idle' ? handleClose : () => {}}>
      <Box
        sx={{
          width: 400,
          height: '100%',
          bgcolor: blueGrey[50],
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          p: '8px',
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            pt: 3,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderRadius: '0 0 4px 4px',
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(137.68deg, #2196f3 10.79%, #7AE7E5 94.57%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <AutoAwesome sx={{ fontSize: 18, color: 'white' }} />
          </Box>

          <Typography variant="h6" sx={{ flex: 1 }}>
            AI Assistant
          </Typography>

          {launchPhase === 'idle' && (
            <IconButton size="small" onClick={handleClose}>
              <Close />
            </IconButton>
          )}
        </Paper>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            bgcolor: 'white',
            p: 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: 'relative',
          }}
        >
          {/* Launch animation overlay */}
          {launchPhase !== 'idle' && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: '#0f172a',
                gap: 4,
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  background: 'linear-gradient(137.68deg, #2196f3 10.79%, #7AE7E5 94.57%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'agentPulse 1.4s ease-in-out infinite',
                  '@keyframes agentPulse': {
                    '0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(33,150,243,0.5)' },
                    '50%': { transform: 'scale(1.12)', boxShadow: '0 0 0 20px rgba(33,150,243,0)' },
                  },
                }}
              >
                <AutoAwesome sx={{ fontSize: 36, color: 'white' }} />
              </Box>

              <Stack gap={2} sx={{ maxWidth: 300 }}>
                {LAUNCH_STEPS.map(({ phase, text }) => {
                  const currentIdx = PHASE_ORDER.indexOf(launchPhase as Exclude<LaunchPhase, 'idle'>);
                  const stepIdx = PHASE_ORDER.indexOf(phase);
                  const isDone = currentIdx > stepIdx;
                  const isActive = currentIdx === stepIdx;

                  return (
                    <Stack key={phase} direction="row" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {isDone ? (
                          <CheckCircleOutline sx={{ fontSize: 18, color: green[400] }} />
                        ) : isActive ? (
                          <CircularProgress size={16} sx={{ color: blue[400] }} />
                        ) : (
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              border: '1.5px solid rgba(255,255,255,0.2)',
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDone ? green[400] : isActive ? 'white' : 'rgba(255,255,255,0.25)',
                          fontWeight: isActive ? 600 : 400,
                          transition: 'color 0.4s ease',
                        }}
                      >
                        {text}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          )}

          {/* Available section */}
          <Stack gap={1}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '22px', letterSpacing: '0.1px' }}>
              Available
            </Typography>

            {/* Qualification Assistant card */}
            <Box
              sx={{
                border: '1px solid #2196f3',
                borderRadius: 2,
                pb: 1.5,
                pt: 1,
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              {/* Title row */}
              <Stack
                direction="row"
                gap={1}
                alignItems="flex-start"
                sx={{ borderBottom: '1px solid', borderColor: 'grey.200', py: 0.5 }}
              >
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Qualification Assistant
                </Typography>

                {/* Status badge */}
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  sx={{
                    bgcolor: statusBadge.bgColor,
                    border: '1px solid',
                    borderColor: statusBadge.borderColor,
                    borderRadius: '4px',
                    px: 1,
                    py: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <statusBadge.Icon sx={{ fontSize: 14, color: statusBadge.textColor }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: '14px',
                      letterSpacing: '0.15px',
                      color: statusBadge.textColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {statusBadge.label}
                  </Typography>
                </Stack>
              </Stack>

              {/* Description */}
              <Typography variant="body2" color="text.secondary">
                {ASSISTANT_DESCRIPTION}
              </Typography>

              {/* Start button + Demo Mode checkbox */}
              {(!hasSession || latestSession?.status !== 'in_progress') && (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    startIcon={<PlayArrow />}
                    disabled={loading}
                    onClick={handleStartAssistant}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Start Assistant
                  </Button>

                  <FormControlLabel
                    label={
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {demoMode && <Warning sx={{ fontSize: 13, color: orange[700] }} />}
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: demoMode ? orange[800] : 'text.secondary',
                            fontWeight: demoMode ? 600 : 400,
                          }}
                        >
                          Demo Mode
                        </Typography>
                      </Stack>
                    }
                    sx={{
                      m: 0,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: demoMode ? orange[300] : 'grey.300',
                      bgcolor: demoMode ? orange[50] : 'transparent',
                      transition: 'border-color 0.2s, background-color 0.2s',
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={demoMode}
                        onChange={e => setDemoMode(e.target.checked)}
                        sx={{ color: demoMode ? orange[600] : undefined, '&.Mui-checked': { color: orange[600] } }}
                      />
                    }
                  />
                </Stack>
              )}

              {/* Details collapsible */}
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: blueGrey[100],
                  pt: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="subtitle2">Details</Typography>
                    <IconButton size="small" sx={{ p: 0 }} onClick={() => setDetailsOpen(prev => !prev)}>
                      {detailsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                    {sessionsLoading && <CircularProgress size={12} />}
                  </Stack>

                  {hasSession && (
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                      onClick={handleOpenMessages}
                      sx={{ textTransform: 'uppercase', py: 0, minWidth: 0 }}
                    >
                      Open Messages
                    </Button>
                  )}
                </Stack>

                {detailsOpen && (
                  <Box>
                    {/* Info row */}
                    <Stack direction="row" gap={0.5} alignItems="flex-start" mb={0.5}>
                      <Info sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                      <Typography variant="body2" color="primary">
                        {hasSession
                          ? 'Qualification details collected by the assistant'
                          : 'Qualification details the assistant will collect'}
                      </Typography>
                    </Stack>

                    {/* Detail items */}
                    {detailItems.map(({ label, subtitle, Icon }, index) => (
                      <Stack
                        key={label}
                        direction="row"
                        gap={1}
                        alignItems="flex-start"
                        sx={{
                          py: 1.5,
                          ...(index < detailItems.length - 1 && {
                            borderBottom: '1px solid',
                            borderColor: 'grey.200',
                          }),
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: blue[50],
                            borderRadius: '4px',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 22, color: 'primary.main' }} />
                        </Box>
                        <Stack gap={0.5}>
                          <Typography variant="subtitle2">{label}</Typography>
                          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                            {subtitle}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Stack>

          {/* Requires Configuration section */}
          <Stack gap={1}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '22px', letterSpacing: '0.1px' }}>
              Requires Configuration
            </Typography>

            <Stack gap={0.5}>
              {REQUIRES_CONFIG_ITEMS.map(name => (
                <Box
                  key={name}
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'grey.200',
                    borderRadius: 2,
                    pb: 1.5,
                    pt: 1,
                    px: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.200', py: 0.5 }}>
                    <Typography variant="subtitle2" color="text.disabled">
                      {name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.disabled">
                    {ASSISTANT_DESCRIPTION}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
